import { APP_ROUTES } from "@/constants/app-routes";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { MixpanelEvents } from "@/constants/mixpanel-events";
import { getLocalStorageJson, setLocalStorageJson } from "@/utils/local-storage";
import { trackMixpanelEvent } from "@/utils/mixpanel";

export const ONBOARDING_V5_VERSION = 5;

export const ONBOARDING_V5_STEPS = [
  { stepId: "name", path: APP_ROUTES.ONBOARDING_FIRST_STEP, stepIndex: 1 },
  { stepId: "diagnostic", path: APP_ROUTES.ONBOARDING_DIAGNOSTIC, stepIndex: 2 },
  { stepId: "goal", path: APP_ROUTES.ONBOARDING_GOAL_STEP, stepIndex: 3 },
  { stepId: "help_with", path: "/profiling/step-help-with", stepIndex: 4 },
  { stepId: "progress_change", path: "/profiling/step-progress-change", stepIndex: 5 },
  { stepId: "starting_from", path: "/profiling/step-starting-from", stepIndex: 6 },
  { stepId: "getting_to", path: "/profiling/step-getting-to", stepIndex: 7 },
  { stepId: "fat_loss", path: "/profiling/step-fat-loss", stepIndex: 8 },
  { stepId: "height", path: "/profiling/step-height", stepIndex: 9 },
  { stepId: "birthday", path: "/profiling/step-birthday", stepIndex: 10 },
  { stepId: "gender", path: "/profiling/step-gender", stepIndex: 11 },
  { stepId: "activity", path: "/profiling/step-activity", stepIndex: 12 },
  { stepId: "workouts", path: "/profiling/step-workouts", stepIndex: 13 },
  { stepId: "move", path: "/profiling/step-move", stepIndex: 14 },
  { stepId: "building", path: "/profiling/step-building", stepIndex: 15 },
  {
    stepId: "building_confirmation",
    path: "/profiling/step-building-confirmation",
    stepIndex: 16,
  },
  { stepId: "app_preview", path: "/profiling/step-app-preview", stepIndex: 17 },
] as const;

export const ONBOARDING_V5_STEP_COUNT = ONBOARDING_V5_STEPS.length;

type OnboardingEntrySource = "post_signup" | "post_signup_resume";

interface OnboardingAttemptState {
  schemaVersion: 1;
  onboardingVersion: typeof ONBOARDING_V5_VERSION;
  attemptId: string;
  userId: string;
  entrySource: OnboardingEntrySource;
  started: boolean;
  completed: boolean;
  viewedStepIds: string[];
  submittedStepIds: string[];
}

interface StartOnboardingInput {
  userId: string | null | undefined;
  isOnboardingCompleted: boolean | undefined;
}

const normalizePathname = (pathname: string) => {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
};

const stepsByPath = new Map(
  ONBOARDING_V5_STEPS.map((step) => [normalizePathname(step.path), step]),
);

const createAttemptId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isAttemptState = (value: unknown): value is OnboardingAttemptState => {
  if (!value || typeof value !== "object") return false;
  const state = value as Partial<OnboardingAttemptState>;

  return (
    state.schemaVersion === 1 &&
    state.onboardingVersion === ONBOARDING_V5_VERSION &&
    typeof state.attemptId === "string" &&
    typeof state.userId === "string" &&
    (state.entrySource === "post_signup" || state.entrySource === "post_signup_resume") &&
    typeof state.started === "boolean" &&
    typeof state.completed === "boolean" &&
    isStringArray(state.viewedStepIds) &&
    isStringArray(state.submittedStepIds)
  );
};

const readAttempt = (): OnboardingAttemptState | null => {
  try {
    const value = getLocalStorageJson(LocalStorageKeys.ONBOARDING_V5_ANALYTICS_KEY);
    return isAttemptState(value) ? value : null;
  } catch {
    console.warn("[Mixpanel] Could not read onboarding analytics state");
    return null;
  }
};

const writeAttempt = (attempt: OnboardingAttemptState) => {
  try {
    setLocalStorageJson(LocalStorageKeys.ONBOARDING_V5_ANALYTICS_KEY, attempt);
  } catch {
    // Analytics bookkeeping must never interrupt registration or onboarding.
    console.warn("[Mixpanel] Could not save onboarding analytics state");
  }
};

const readCurrentUserId = (): string | null => {
  try {
    const user = getLocalStorageJson(LocalStorageKeys.USER_KEY);
    if (!user || typeof user !== "object" || !("id" in user)) return null;
    return typeof user.id === "string" ? user.id : null;
  } catch {
    return null;
  }
};

const readActiveAttempt = () => {
  const attempt = readAttempt();
  if (!attempt || attempt.completed || !attempt.started) return null;
  return readCurrentUserId() === attempt.userId ? attempt : null;
};

// The mirror of readActiveAttempt for post-completion milestones (e.g. the Meet
// welcome screen): the attempt is finalized by then, so it must be completed —
// still scoped to the signed-in user to avoid cross-user leakage.
const readCompletedAttempt = () => {
  const attempt = readAttempt();
  if (!attempt || !attempt.completed) return null;
  return readCurrentUserId() === attempt.userId ? attempt : null;
};

const createAttempt = (
  userId: string,
  entrySource: OnboardingEntrySource,
): OnboardingAttemptState => ({
  schemaVersion: 1,
  onboardingVersion: ONBOARDING_V5_VERSION,
  attemptId: createAttemptId(),
  userId,
  entrySource,
  started: false,
  completed: false,
  viewedStepIds: [],
  submittedStepIds: [],
});

const getAttemptProperties = (attempt: OnboardingAttemptState) => ({
  onboarding_version: ONBOARDING_V5_VERSION,
  onboarding_attempt_id: attempt.attemptId,
  entry_source: attempt.entrySource,
});

const getStepProperties = (
  attempt: OnboardingAttemptState,
  step: (typeof ONBOARDING_V5_STEPS)[number],
) => ({
  ...getAttemptProperties(attempt),
  step_id: step.stepId,
  step_index: step.stepIndex,
  step_count: ONBOARDING_V5_STEP_COUNT,
});

/** Starts the post-signup attempt after the backend account and Mixpanel identity exist. */
export const trackOnboardingV5AccountCreated = (userId: string) => {
  const existingAttempt = readAttempt();
  if (
    existingAttempt?.userId === userId &&
    existingAttempt.entrySource === "post_signup" &&
    !existingAttempt.completed
  ) {
    return;
  }

  const attempt = createAttempt(userId, "post_signup");
  writeAttempt(attempt);
  trackMixpanelEvent(MixpanelEvents.ACCOUNT_CREATED, getAttemptProperties(attempt));
};

/** Starts only a first-time onboarding journey; completed-user restarts are ignored. */
export const trackOnboardingV5Started = ({
  userId,
  isOnboardingCompleted,
}: StartOnboardingInput) => {
  if (!userId) return;

  const existingAttempt = readAttempt();
  if (existingAttempt?.userId === userId && existingAttempt.completed) return;

  if (isOnboardingCompleted === true) {
    if (existingAttempt?.userId === userId && !existingAttempt.completed) {
      writeAttempt({ ...existingAttempt, completed: true });
    }
    return;
  }

  let attempt =
    existingAttempt?.userId === userId && !existingAttempt.completed ? existingAttempt : undefined;

  // A missing attempt is a returning, incomplete signup. Wait for an explicit
  // false so a completed user's restart cannot enter the acquisition funnel
  // while their backend status is still loading.
  if (!attempt) {
    if (isOnboardingCompleted !== false) return;
    attempt = createAttempt(userId, "post_signup_resume");
  }

  if (attempt.started) return;

  const startedAttempt = { ...attempt, started: true };
  writeAttempt(startedAttempt);
  trackMixpanelEvent(MixpanelEvents.ONBOARDING_STARTED, getAttemptProperties(startedAttempt));
};

export const trackOnboardingV5StepViewed = (pathname: string) => {
  const step = stepsByPath.get(normalizePathname(pathname));
  const attempt = readActiveAttempt();
  if (!step || !attempt || attempt.viewedStepIds.includes(step.stepId)) return;

  writeAttempt({
    ...attempt,
    viewedStepIds: [...attempt.viewedStepIds, step.stepId],
  });
  trackMixpanelEvent(MixpanelEvents.ONBOARDING_STEP_VIEWED, getStepProperties(attempt, step));
};

export const trackOnboardingV5StepSubmitted = (pathname: string) => {
  const step = stepsByPath.get(normalizePathname(pathname));
  const attempt = readActiveAttempt();
  if (!step || !attempt || attempt.submittedStepIds.includes(step.stepId)) return;

  writeAttempt({
    ...attempt,
    submittedStepIds: [...attempt.submittedStepIds, step.stepId],
  });
  trackMixpanelEvent(MixpanelEvents.ONBOARDING_STEP_SUBMITTED, getStepProperties(attempt, step));
};

/**
 * Records the option IDs chosen on the "What would you like me to help with?" step.
 * The funnel events (viewed/submitted) intentionally carry no answer values, so this
 * dedicated, attempt-correlated event is where the actual selections are captured.
 */
export const trackOnboardingV5HelpWithSelected = (helpWith: string[]) => {
  const attempt = readActiveAttempt();
  if (!attempt) return;

  trackMixpanelEvent(MixpanelEvents.ONBOARDING_HELP_WITH_SELECTED, {
    ...getAttemptProperties(attempt),
    help_with: helpWith,
  });
};

/** Records the final submission and completion once product completion has succeeded. */
export const trackOnboardingV5Completed = () => {
  const appPreviewStep = ONBOARDING_V5_STEPS[ONBOARDING_V5_STEPS.length - 1];
  let attempt = readActiveAttempt();
  if (!attempt) return;

  if (!attempt.submittedStepIds.includes(appPreviewStep.stepId)) {
    attempt = {
      ...attempt,
      submittedStepIds: [...attempt.submittedStepIds, appPreviewStep.stepId],
    };
    writeAttempt(attempt);
    trackMixpanelEvent(
      MixpanelEvents.ONBOARDING_STEP_SUBMITTED,
      getStepProperties(attempt, appPreviewStep),
    );
  }

  writeAttempt({ ...attempt, completed: true });
  trackMixpanelEvent(MixpanelEvents.ONBOARDING_COMPLETED, getAttemptProperties(attempt));
};

/**
 * Records the user leaving the post-completion "Meet your NutriCoach" welcome
 * screen into the app. Onboarding is already finalized on app-preview, so this is
 * a dedicated event correlated to the completed attempt rather than a funnel step
 * — it measures whether users proceed from the welcome screen or bounce.
 */
export const trackOnboardingV5WelcomeStarted = () => {
  const attempt = readCompletedAttempt();
  if (!attempt) return;

  trackMixpanelEvent(MixpanelEvents.ONBOARDING_WELCOME_STARTED, getAttemptProperties(attempt));
};
